import type { OrganizationFeatureConfig } from 'src/services/organization-feature-config'

export type ChannelKey = 'in_app' | 'email' | 'whatsapp'

/** Why a channel can't be offered — drives the "unavailable" reason shown in the UI. */
export type UnavailableReason = 'org' | 'phone'

export type ChannelAvailability =
  | { available: true }
  | { available: false; reason: UnavailableReason }

/**
 * Decides whether a notification channel can be offered to the user.
 *
 * `in_app` is always available. `email` and `whatsapp` require the matching org
 * feature flag to be explicitly enabled; `whatsapp` additionally requires a phone
 * number. While the feature config is still loading (`undefined`), org-gated
 * channels are treated as unavailable so nothing defaults to on before it resolves.
 */
export function getChannelAvailability({
  channel,
  featureConfig,
  hasPhoneNumber,
}: {
  channel: ChannelKey
  featureConfig: OrganizationFeatureConfig | undefined
  hasPhoneNumber: boolean
}): ChannelAvailability {
  if (channel === 'in_app') return { available: true }

  if (channel === 'email') {
    return featureConfig?.emailNotificationsEnabled === true
      ? { available: true }
      : { available: false, reason: 'org' }
  }

  // whatsapp
  if (featureConfig?.whatsappNotificationsEnabled !== true) {
    return { available: false, reason: 'org' }
  }
  if (!hasPhoneNumber) {
    return { available: false, reason: 'phone' }
  }
  return { available: true }
}
